import React, { useState, useEffect, useRef, useCallback } from 'react';
import StrainViewer from './StrainViewer';

export default function StrainReel({ strains }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImageIndex, setViewerImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);
  const touchRef = useRef({ startX: 0, startY: 0 });

  // Flatten all strain images with their strain info for the reel
  const allSlides = [];
  strains.forEach(strain => {
    if (strain.images && strain.images.length > 0) {
      strain.images.forEach(img => {
        allSlides.push({
          ...img,
          strainName: strain.name,
          strainType: strain.type,
          testDescription: strain.test_description,
          strainImages: strain.images,
          strainImageStartIndex: allSlides.length,
        });
      });
    }
  });

  const slideCount = allSlides.length;

  const goNext = useCallback(() => {
    if (slideCount <= 1) return;
    setCurrentIndex(prev => (prev + 1) % slideCount);
  }, [slideCount]);

  const goPrev = useCallback(() => {
    if (slideCount <= 1) return;
    setCurrentIndex(prev => (prev - 1 + slideCount) % slideCount);
  }, [slideCount]);

  // Auto-rotation
  useEffect(() => {
    if (isPaused || viewerOpen || slideCount <= 1) {
      clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(goNext, 10000);
    return () => clearInterval(timerRef.current);
  }, [isPaused, viewerOpen, slideCount, goNext]);

  // Touch/pointer handlers for swipe
  const handlePointerDown = (e) => {
    touchRef.current = { startX: e.clientX, startY: e.clientY };
  };

  const handlePointerUp = (e) => {
    const dx = e.clientX - touchRef.current.startX;
    const dy = e.clientY - touchRef.current.startY;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  const handleImageClick = (slideIndex) => {
    const slide = allSlides[slideIndex];
    // Find which image within this strain's images array was clicked
    const strainImgs = slide.strainImages;
    const imgIdx = strainImgs.findIndex(i => i.id === slide.id);
    setViewerImageIndex(imgIdx >= 0 ? imgIdx : 0);
    setViewerOpen(true);
  };

  if (slideCount === 0) {
    return (
      <div style={{
        background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
        padding: '24px 16px', textAlign: 'center', marginBottom: 16,
        border: '1px solid var(--border)',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>&#128247;</div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No strain photos yet</div>
      </div>
    );
  }

  const currentSlide = allSlides[currentIndex];

  return (
    <>
      <div
        style={{
          position: 'relative', borderRadius: 'var(--radius-sm)', overflow: 'hidden',
          marginBottom: 16, background: '#000', cursor: 'pointer', userSelect: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {/* Current image */}
        <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
          {allSlides.map((slide, i) => (
            <img
              key={slide.id}
              src={slide.url}
              alt={slide.strainName}
              onClick={() => handleImageClick(i)}
              loading={i === 0 ? 'eager' : 'lazy'}
              style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                objectFit: 'cover', transition: 'opacity 0.5s ease',
                opacity: i === currentIndex ? 1 : 0,
                pointerEvents: i === currentIndex ? 'auto' : 'none',
              }}
            />
          ))}
        </div>

        {/* Strain name overlay */}
        <div style={{
          position: 'absolute', top: 10, left: 12,
          color: '#fff', fontSize: 15, fontWeight: 700,
          textShadow: '0 1px 4px rgba(0,0,0,0.7)',
          pointerEvents: 'none',
        }}>
          {currentSlide.strainName}
        </div>

        {/* Test description overlay */}
        {currentSlide.testDescription && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
            padding: '20px 12px 10px', pointerEvents: 'none',
          }}>
            <div style={{
              color: 'rgba(255,255,255,0.9)', fontSize: 12, lineHeight: 1.4,
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {currentSlide.testDescription}
            </div>
          </div>
        )}

        {/* Navigation arrows */}
        {slideCount > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              style={{
                position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none',
                borderRadius: '50%', width: 30, height: 30, cursor: 'pointer',
                fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              &#8249;
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none',
                borderRadius: '50%', width: 30, height: 30, cursor: 'pointer',
                fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              &#8250;
            </button>
          </>
        )}

        {/* Dot indicators */}
        {slideCount > 1 && slideCount <= 12 && (
          <div style={{
            position: 'absolute', bottom: currentSlide.testDescription ? 36 : 8,
            left: '50%', transform: 'translateX(-50%)',
            display: 'flex', gap: 5, pointerEvents: 'none',
          }}>
            {allSlides.map((_, i) => (
              <div
                key={i}
                style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: i === currentIndex ? '#fff' : 'rgba(255,255,255,0.4)',
                  transition: 'background 0.3s',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Full-screen viewer */}
      {viewerOpen && (
        <StrainViewer
          images={currentSlide.strainImages}
          initialIndex={viewerImageIndex}
          strainName={currentSlide.strainName}
          testDescription={currentSlide.testDescription}
          onClose={() => setViewerOpen(false)}
        />
      )}
    </>
  );
}
